import React, { useState, useEffect } from 'react';
import { callAI } from '../services/aiService';
import { storageService, Inspiration as InspirationData } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface TrendTopic {
  title: string;
  description: string;
  icon: string;
}

interface IdeaResult {
  keyword: string;
  ideas: string[];
}

const STORAGE_KEY_KEYWORD = 'inspiration_keyword';
const STORAGE_KEY_TRENDS = 'inspiration_trends';
const STORAGE_KEY_IDEAS = 'inspiration_idea_result';

export const Inspiration: React.FC = () => {
  const { t, locale } = useT();
  const [keyword, setKeyword] = useState(() => localStorage.getItem(STORAGE_KEY_KEYWORD) || '');
  const [trends, setTrends] = useState<TrendTopic[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_TRENDS);
    return saved ? JSON.parse(saved) : [];
  });
  const [ideaResult, setIdeaResult] = useState<IdeaResult | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_IDEAS);
    return saved ? JSON.parse(saved) : null;
  });
  const [trendStatus, setTrendStatus] = useState<'idle' | 'generating' | 'complete' | 'error'>('idle');
  const [ideaStatus, setIdeaStatus] = useState<'idle' | 'generating' | 'complete' | 'error'>('idle');
  const [savedInspirations, setSavedInspirations] = useState<InspirationData[]>(storageService.getInspirations());
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY_KEYWORD, keyword); }, [keyword]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_TRENDS, JSON.stringify(trends)); }, [trends]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_IDEAS, JSON.stringify(ideaResult)); }, [ideaResult]);

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const generateTrends = async () => {
    setTrendStatus('generating');
    setError(null);
    try {
      const prompt = `Suggest 5 trending topics on Twitter right now that are likely to get engagement. Focus on tech, AI, and creator economy.

【Output format (JSON)】
{
  "topics": [
    { "title": "Topic name", "description": "Why it's trending right now (1 sentence)", "icon": "one fitting emoji" }
  ]
}`;
      const response = await callAI(
        [{ role: 'system', content: 'You are a Twitter trends expert. Suggest hot topics in tech and creator space.' }, { role: 'user', content: prompt }],
        { temperature: 0.9, maxTokens: 1000, jsonMode: true }
      );
      const parsed = JSON.parse(response);
      setTrends(parsed.topics || []);
      setTrendStatus('complete');
    } catch (err) {
      setTrendStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to generate trends');
    }
  };

  const generateIdeas = async (searchKeyword?: string) => {
    const kw = searchKeyword || keyword;
    if (!kw.trim()) return;
    setIdeaStatus('generating');
    setError(null);
    try {
      const prompt = `Generate 10 tweet ideas from the keyword "${kw}".

【Rules】
1. Each idea is a 1-line summary (under 50 characters)
2. Variety: personal stories, tips, questions, hot takes, etc.
3. Make them compelling enough to actually tweet

【Output format (JSON)】
{ "ideas": ["idea 1", "idea 2", ..., "idea 10"] }`;
      const response = await callAI(
        [{ role: 'system', content: 'You are a social media content ideation expert. Generate viral tweet ideas.' }, { role: 'user', content: prompt }],
        { temperature: 0.9, maxTokens: 1000, jsonMode: true }
      );
      const parsed = JSON.parse(response);
      const ideas: string[] = parsed.ideas || [];
      setIdeaResult({ keyword: kw, ideas });
      setIdeaStatus('complete');

      const inspirationItem: InspirationData = {
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        topic: kw, ideas, timestamp: new Date().toISOString(),
      };
      storageService.addInspiration(inspirationItem);
      setSavedInspirations(storageService.getInspirations());
      storageService.addHistoryItem({ type: 'inspiration', content: ideas.slice(0, 5).join('\n'), metadata: { keyword: kw } });
    } catch (err) {
      setIdeaStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to generate ideas');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[900px] mx-auto">
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">{t('inspiration.title')}</h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">{t('inspiration.subtitle')}</p>
      </div>

      {/* Trend Topics */}
      <div className="mb-8 lg:mb-10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg lg:text-xl m-0 text-text-primary">{t('inspiration.trendingTopics')}</h2>
          <button
            onClick={generateTrends}
            disabled={trendStatus === 'generating'}
            className={cn(
              'px-5 py-2.5 border-none rounded-2xl text-sm font-bold transition-all',
              trendStatus === 'generating'
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-accent to-brand text-white cursor-pointer hover:opacity-90'
            )}
          >
            {trendStatus === 'generating' ? t('common.loading') : t('inspiration.getTrends')}
          </button>
        </div>

        {trends.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {trends.map((topic, index) => (
              <div
                key={index}
                onClick={() => { setKeyword(topic.title); generateIdeas(topic.title); }}
                className="p-4 bg-surface-2 border border-border rounded-xl cursor-pointer transition-all hover:border-accent hover:bg-accent/5"
              >
                <div className="text-[28px] mb-2">{topic.icon}</div>
                <div className="text-base font-bold text-text-primary mb-1">{topic.title}</div>
                <div className="text-[13px] text-text-muted leading-[1.4]">{topic.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Keyword Search */}
      <div className="mb-8 lg:mb-10">
        <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('inspiration.generateFromKeyword')}</h2>
        <div className="flex gap-2.5">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && generateIdeas()}
            placeholder={t('inspiration.keywordPlaceholder')}
            className="flex-1 px-4 lg:px-5 py-4 bg-surface-2 border border-border rounded-xl text-text-primary text-base font-[inherit] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />
          <button
            onClick={() => generateIdeas()}
            disabled={!keyword.trim() || ideaStatus === 'generating'}
            className={cn(
              'px-6 lg:px-8 py-4 border-none rounded-xl text-base font-bold whitespace-nowrap transition-all',
              !keyword.trim() || ideaStatus === 'generating'
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-accent to-brand text-white cursor-pointer hover:opacity-90'
            )}
          >
            {ideaStatus === 'generating' ? t('common.generating') : t('inspiration.generate')}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-surface-2 border border-danger rounded-xl mb-6">
          <p className="text-danger m-0">{error}</p>
        </div>
      )}

      {/* Idea Results */}
      {ideaResult && (
        <div className="mb-8 lg:mb-10">
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('inspiration.ideasFor', { keyword: ideaResult.keyword })}</h2>
          <div className="flex flex-col gap-2.5">
            {ideaResult.ideas.map((idea, index) => (
              <div
                key={index}
                onClick={() => handleCopy(idea, index)}
                className="py-3 px-4 bg-surface-2 border border-border rounded-lg cursor-pointer flex items-center gap-3 transition-all hover:border-accent"
              >
                <span className="w-6 h-6 flex items-center justify-center bg-accent rounded-full text-xs font-bold text-white flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-[15px] text-text-primary leading-[1.4] flex-1">
                  {copiedIndex === index ? `✓ ${t('common.copied')}` : idea}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saved Inspirations */}
      {savedInspirations.length > 0 && (
        <div>
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('inspiration.savedInspirations')}</h2>
          <div className="flex flex-col gap-3">
            {savedInspirations.slice(0, 10).map((item) => (
              <div key={item.id} className="p-4 bg-surface-2 border border-border rounded-xl">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-bold text-accent">{item.topic}</span>
                  <span className="text-xs text-text-muted">
                    {new Date(item.timestamp).toLocaleDateString(locale === 'ja' ? 'ja-JP' : 'en-US')}
                  </span>
                </div>
                <div className="text-[13px] text-text-muted">
                  {item.ideas.slice(0, 3).join(' / ')}
                  {item.ideas.length > 3 && ` ${t('inspiration.more', { count: item.ideas.length - 3 })}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
